import { useLayoutContext } from '../Contexts/LayoutContext';
import { useOrderTypeContext } from '../Contexts/OrderTypeContext';
import { useStoreContext } from '../Contexts/StoreContext';
import DOMPurify from 'dompurify';
import { compareEquals, generateOperatingHoursText, orderTypeAsSlug } from '../Utils/Helpers';
import StoreService from '../Services/StoreService';
import { format } from 'date-fns';
import { CASHIER_STATUS_OPEN } from '../Utils/Constants/Checkout';
import { useLoginContext } from '../Contexts/LoginContext';
import { useCheckoutContext } from '../Contexts/CheckoutContext';
import CartService from '../Services/CartService';
import { useCartContext } from '../Contexts/CartContext';
import { ERROR_CART_NOT_FOUND } from '../Utils/Constants/Cart';
import { ORDER_TYPE_DIRECT_SALE } from '../Utils/Constants/Service';

export const useStoreHook = () => {

  const { setMainLoading, setMainLoadingIcon, setMainLoadingIconError, setMainLoadingTitle, setMainLoadingMessage, setMainLoadingCloseButton,setMainLoadingIconNoSearchMap } = useLayoutContext();
  const { currentStore, checkStoreOpen, setCurrentStore, setLoadingStore, setCashier, setRequiredTable, setTable, setTables } = useStoreContext();
  const { orderType, getOrderTypeIdByName } = useOrderTypeContext();
  const { setGeolocation, token, setGuestToken, guestToken } = useLoginContext();
  const { setShowPixCode } = useCheckoutContext();
  const { setCurrentCart, setLoadingCurrentCart, setCartDisplay, cartDisplay } = useCartContext();

  const loadStoreInfo = async (storeId: number | string) => {
    setLoadingStore(true);
    try {
        const storeSevice = new StoreService(orderType);
        const payload = {store_id: storeId}
        const getStoreInfo = await storeSevice.info(payload);
        if (!compareEquals(getStoreInfo, currentStore)) {
          setCurrentStore(getStoreInfo);
        }
        return getStoreInfo;
    } catch (error) {
      setTimeout(() => {
        setMainLoading(true);
        setMainLoadingIcon(false);
        setMainLoadingIconError(false);
        setMainLoadingIconNoSearchMap(true);
        setMainLoadingCloseButton(true);
        const title = DOMPurify.sanitize(`Não conseguimos localizar a loja <strong class='text-gray-600'>"${storeId}"</strong>. Verifique e tente novamente.`);
        setMainLoadingTitle('Nenhuma loja encontrada');
        setMainLoadingMessage(title);
        setCurrentStore(null);
      }, 500);
      throw error;
    } finally {
      setLoadingStore(false);
    }
  }

  const createGuestToken = async () => {
    try {
      const cartService = new CartService(orderType);
      const orderTypeId = Number(getOrderTypeIdByName(orderType));
      const isDirectSale = orderType === ORDER_TYPE_DIRECT_SALE;

      const payload = {
        store_id: currentStore.id, order_type: orderTypeId,
        ...(isDirectSale && { is_direct_sale: true })
      }
      const getGuestToken = await cartService.guestToken(payload);
      if (getGuestToken.token) {
        setGuestToken(getGuestToken.token);
      }
      return getGuestToken;
    } catch (error) {
      throw error;
    }
  }

  const createGuestCart = async () => {
    try {
      setLoadingCurrentCart(true)
      const cartService = new CartService(orderType, token, guestToken);
      const payload = {store_id: currentStore.id, order_type_id: Number(getOrderTypeIdByName(orderType)), order_type: orderType};
      const getGuestCart = await cartService.createGuestCart(payload);
      if (getGuestCart) {
        setCurrentCart(getGuestCart);
        setLoadingCurrentCart(false);
        setCartDisplay(null); // Resetando o display do carrinho do autoatendimento
      }
    } catch (error: any) {
      setGuestToken(null);
      setLoadingCurrentCart(false);

      if (error.response?.data?.error === ERROR_CART_NOT_FOUND) {
        setCurrentCart(null);
      }
    }
  }

  const storeIsOpen = () => {
    const orderTypeId = Number(getOrderTypeIdByName(orderType));
    if (!orderType || isNaN(orderTypeId)) {
      console.warn('Tipo de pedido inválido.');
      return false;
    }
    if (!currentStore || !Array.isArray(currentStore.store_workdays)) {
      console.warn('Informações da loja inválidas.');
      return false;
    }
    if (!checkStoreOpen(orderTypeId)) {
      setTimeout(() => {
        setMainLoading(true);
        setMainLoadingIcon(false);
        setMainLoadingIconError(true);
        setMainLoadingCloseButton(true);
        const storeWorkdays = currentStore.store_workdays.filter((cw: any) => cw.service_type === orderTypeId && cw.active);
        const operatingHoursMessage = DOMPurify.sanitize(generateOperatingHoursText(storeWorkdays));
        const hasWorkdays = storeWorkdays.length > 0;
        setMainLoadingTitle(
          `Nossa loja está fechada no momento.` +
          (hasWorkdays ? ' Confira os horários de funcionamento.' : '')
        );
        setMainLoadingMessage(operatingHoursMessage);
        loadStoreInfo(currentStore.id).catch(err => console.error('Erro ao recarregar loja:', err));
      }, 250);
      return false;
    }
    return true;
  };

  const noOpenCashier = () => {
      setMainLoading(true);
      setMainLoadingIcon(false);
      setMainLoadingIconError(true);
      setMainLoadingIconNoSearchMap(false);
      setMainLoadingCloseButton(true);
      setMainLoadingTitle('Opa! Infelizmente, nossa loja está fechada no momento.');
      setMainLoadingMessage('');

      //* Removendo informações do usuário/pagamento
      setGeolocation(null);
      setTable(null);
      setShowPixCode(false);
  }

  const getCashier = async () => {
      const payload = {
          store_id: currentStore.id,
          user: true,
          sort: 'ASC',
          date_from: format(new Date(), 'yyyy-MM-dd'),
          date_to: format(new Date(), 'yyyy-MM-dd'),
          status: CASHIER_STATUS_OPEN
      }

      try {
          const storeService = new StoreService(orderType);
          const getCashiers = await storeService.cashiers(payload);

          if (getCashiers && Array.isArray(getCashiers) && getCashiers.length > 0) {
              setCashier(getCashiers[0]);
              return getCashiers[0];
          } else {
            setCashier(null);
            return null;
          }
        } catch (error) {
          console.error('Error cashiers:', error);
          return null;
        }
  }

  const getLinkToHome = (): string => {
    if (orderType && currentStore && currentStore.slug) {
        return `/${orderTypeAsSlug(orderType)}/${currentStore.slug}`;
    } else {
        return '/';
    }
  }

  const loadTables = async () => {
    try {
        const storeSevice = new StoreService(orderType, token);
        const payload = {
            store_id: currentStore.id,
            tables: true
        }
        const tables = await storeSevice.tables(payload);
        if (tables) {
            setTables(tables)
            return tables;
        }
    } catch (error) {
      throw error;
    }
  }

  const updateCartTable = async (table_id: number) => {
    const payload = {
      cart_id: cartDisplay.id,
      order_type: Number(getOrderTypeIdByName(orderType)),
      store_id: currentStore.id,
      store_table_id: table_id,
    }
    try {
        const cartService = new CartService(orderType, token, guestToken);
        const response = await cartService.updateCartTable(cartDisplay.id, payload);
        if (response) {
          setRequiredTable(false);
        }
    } catch (error) {
      console.log(error)
    }
  }

  return {
    loadStoreInfo,
    storeIsOpen,
    getCashier,
    noOpenCashier,
    createGuestToken,
    createGuestCart,
    getLinkToHome,
    loadTables,
    updateCartTable
  };
}


